import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User as UserIcon, ChefHat, MapPin, Plane, UtensilsCrossed,
  BarChart3, Image as ImageIcon, GraduationCap, Settings as SettingsIcon, X, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { SettingsSheet } from "@/components/SettingsButton";

// ─────────────────────────────────────────────────────────────────────────────
//  MoreSheet — bottom-sheet "app launcher" that replaces the You tab.
//
//  Active items navigate immediately. Coming-soon items show a friendly
//  toast and a "Soon" badge so users can SEE the roadmap without leaving
//  the menu. Grid layout deliberately mirrors the iOS Settings home
//  grid — fastest-recognised pattern on iPhone.
// ─────────────────────────────────────────────────────────────────────────────

const ITEMS = [
  { key: "you",        label: "You",            icon: UserIcon,        route: "/you",          active: true  },
  { key: "memories",   label: "Memories",       icon: ImageIcon,       route: "/remember",     active: true  },
  { key: "study",      label: "Study Planner",  icon: GraduationCap,   route: "/study",        active: true  },
  { key: "settings",   label: "Settings",       icon: SettingsIcon,    active: true, openSettings: true },
  { key: "recipes",    label: "Recipe Box",     icon: ChefHat,         route: "/recipes",      active: true  },
  { key: "places",     label: "Places",         icon: MapPin,          active: false, hint: "Where your family hangs out" },
  { key: "meal_plan",  label: "Meal Planner",   icon: UtensilsCrossed, active: false, hint: "Recipes → calendar → shopping list" },
  { key: "vacations",  label: "Vacations",      icon: Plane,           active: false, hint: "Plan trips with the family" },
  { key: "insights",   label: "Insights",       icon: BarChart3,       active: false, hint: "Family time, screen time, balance" },
];

export function MoreSheet({ open, onClose }) {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);

  if (!open && !showSettings) return null;

  const onTap = (item) => {
    if (!item.active) {
      toast.message(`${item.label} — coming soon`, {
        description: item.hint,
      });
      return;
    }
    if (item.openSettings) {
      setShowSettings(true);
      return;
    }
    onClose?.();
    navigate(item.route);
  };

  return (
    <>
      {open && (
        <div
          data-testid="more-sheet"
          className="fixed inset-0 z-[55] flex items-end justify-center bg-foreground/40 backdrop-blur-sm"
          onClick={onClose}
        >
          <div
            className="w-full max-w-[480px] bg-background rounded-t-3xl pb-safe"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between px-5 pt-4 pb-2">
              <h2 className="font-display text-[18px] font-medium tracking-tight">More</h2>
              <button
                onClick={onClose}
                data-testid="more-close"
                className="w-9 h-9 flex items-center justify-center text-muted-foreground tap"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="px-4 pb-6 grid grid-cols-2 gap-3" data-testid="more-grid">
              {ITEMS.map((item) => (
                <Tile key={item.key} item={item} onTap={() => onTap(item)} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings sheet rendered ABOVE the More sheet — sibling, not nested,
          so backdrop dismissal works correctly and z-indexes don't fight. */}
      <SettingsSheet open={showSettings} onClose={() => setShowSettings(false)} />
    </>
  );
}

function Tile({ item, onTap }) {
  const Icon = item.icon;
  return (
    <button
      data-testid={`more-tile-${item.key}`}
      data-active={item.active ? "true" : "false"}
      onClick={onTap}
      className={[
        "relative rounded-2xl p-4 border bg-card text-left tap transition-colors",
        item.active ? "border-border active:bg-muted/40" : "border-border/60 opacity-80",
      ].join(" ")}
    >
      <div className={[
        "w-11 h-11 rounded-xl flex items-center justify-center mb-3",
        item.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
      ].join(" ")}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-[15px] font-medium leading-tight">{item.label}</span>
        {item.active ? (
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[9px] uppercase tracking-wider font-medium flex-shrink-0">
            Soon
          </span>
        )}
      </div>
      {item.hint && (
        <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{item.hint}</p>
      )}
    </button>
  );
}
